import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import { App, User } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Route, Switch, useHistory, withRouter } from "react-router-dom";
import Popup from "reactjs-popup";

import { useStateValue } from "../config/AppContext";
import CatalogClass from "../models/Catalog";
import SettingsMenu from "./Menus/SettingsMenu";
import BaC from "./Utilities/BackArrow";
import EditToggle from "./Utilities/EditToggle";
import Icons from "./Utilities/Icons";

type HeaderProps = {
  [x: string]: any;
};

const HeaderComponent: React.FC<HeaderProps> = (props) => {
  const { t } = useTranslation();
  const { ic_logo } = Icons;
  const {
    Methods: { setAppState },
    clients,
    catalog,
    companies,
  } = useStateValue() as {
    Methods: App.Methods;
    clients: App.State["clients"];
    catalog: CatalogClass;
    companies: App.State["companies"];
  };
  const history = useHistory();
  const currentPath = props.location.pathname.replace(/^\//, "");
  const [toggle, setToggle] = React.useState(/settings/.test(currentPath));
  const [edit, setEdit] = React.useState(false);

  // Set header text
  const header = () => {
    let id: string;
    let client: User.IUserClient | undefined;
    switch (true) {
      case currentPath === "":
        return "dashboard";
      case /clients\/edit/.test(currentPath):
        return "edit client";
      case /clients\/add/.test(currentPath):
        return "add client";
      case /clients\/sessions/.test(currentPath):
        if (!clients) return;
        id = currentPath.replace("clients/sessions/", "");
        client = clients.find((el) => el.id === id);
        if (!client) return `Client Sessions`;
        return `${t("Session with")} ${client.first_name} ${client.last_name}`;
      case /clients\//.test(currentPath):
        if (!clients) return;
        id = currentPath.replace("clients/", "");
        client = clients.find((el: any) => el.id === id);
        if (!client) return "clients";
        return ``;
      case /exercises\/add/.test(currentPath):
        return "add exercise";
      case /exercises\/edit/.test(currentPath):
        return `${t("Edit Exercise")}`;
      case /exercises\//.test(currentPath):
        id = props.location.pathname.replace(/\/exercises\//, "");
        const exercise = catalog?.findExercise(id);
        if (!exercise) return "exercise details";
        return `${exercise.name}`;
      case /workouts\/add/.test(currentPath):
        return "add workout";
      case /workouts\/edit/.test(currentPath):
        return "edit workout";
      case /workouts\/share/.test(currentPath):
        return "share workout";
      case /workouts\//.test(currentPath):
        id = props.location.pathname.replace(/\/workouts\//, "");
        const workout = catalog && catalog.Workouts.find((el) => el.id === id);
        if (!workout) return "workout details";
        return `${workout.name}`;
      case /settings\//.test(currentPath):
        return currentPath.replace("settings", "").replace(/\//, "  ");
      case /schedules\//.test(currentPath):
        id = props.location.pathname.replace(/\/schedules\//, "");
        return "schedule details";
      default:
        return currentPath.replace(/\//, "  ");
    }
  };

  // Set header icons
  const iconSpan = () =>
    window.innerWidth > 700 ? (
      <Popup
        position={"bottom right"}
        offsetX={-35}
        contentStyle={{
          width: 340,
          borderRadius: "5px",
        }}
        trigger={
          <span className="settings" onClick={() => setToggle(true)}>
            {/* <img alt='settings' src={ic_settings} /> */}
            <SettingsOutlinedIcon
              style={{
                fontSize: 35,
                color: "var(--text-primary)",
                stroke: "#ffffff",
              }}
            />
          </span>
        }
        arrowStyle={{ display: "none" }}
      >
        {(close) => <SettingsMenu close-menu={close} />}
      </Popup>
    ) : (
      <Switch>
        <Route
          strict
          path={[
            "/clients/add",
            "/clients/edit",
            "/exercises/add",
            "/exercises/edit",
            "/workouts/add",
            "/workouts/edit",
            "/settings",
          ]}
          render={() => <></>}
        />
        <Route
          path="/clients/sessions"
          render={() => (
            <EditToggle
              id={currentPath.replace(/clients\/sessions\//, "")}
              show={!edit}
              action={setAppState}
              currentPath="client-sessions"
            />
          )}
        />
        <Route
          path="/clients/:id"
          render={() => (
            <EditToggle
              id={currentPath.replace(/clients\//, "")}
              show={!edit}
              action={() => setEdit(false)}
              currentPath="clients"
            />
          )}
        />
        <Route
          exact
          path="/exercises/:id"
          render={() => {
            const exerciseId = props.location.pathname.replace(/\/exercises\//, "");
            const exercise = catalog?.findExercise(exerciseId);
            if (!exercise) return;
            return (
              <EditToggle
                id={exerciseId}
                show={!edit}
                action={() => setEdit(false)}
                currentPath="exercises"
              />
            );
          }}
        />
        <Route
          exact
          path="/workouts/:id"
          render={() => {
            const workoutId = props.location.pathname.replace(/\/workouts\//, "");
            const workout = catalog && catalog.Workouts.find((el: any) => el.id === workoutId);
            if (!workout) return;
            return (
              <EditToggle
                id={workoutId}
                show={!edit}
                action={() => setEdit(false)}
                currentPath="workouts"
              />
            );
          }}
        />
        <Route
          exact
          path={["/", "/clients", "/schedules", "/workouts", "/exercises"]}
          render={() =>
            window.innerWidth > 700 ? (
              <Popup
                position={"bottom right"}
                offsetX={-35}
                contentStyle={{
                  width: 340,
                  borderRadius: "5px",
                }}
                trigger={
                  <span className="settings" onClick={() => setToggle(true)}>
                    {/* <img alt='settings' src={ic_settings} /> */}
                    <SettingsOutlinedIcon
                      style={{
                        fontSize: 35,
                        color: "var(--text-primary)",
                        stroke: "#ffffff",
                      }}
                    />
                  </span>
                }
                arrowStyle={{ display: "none" }}
              >
                <SettingsMenu />
              </Popup>
            ) : (
              <NavLink to="/settings">
                <span className="settings" onClick={() => setToggle(true)}>
                  <SettingsOutlinedIcon
                    style={{
                      fontSize: 35,
                      color: "var(--text-primary)",
                      stroke: "#ffffff",
                    }}
                  />
                </span>
              </NavLink>
            )
          }
        />
      </Switch>
    );

  // Handle back clicks
  const handleBack = () => {
    switch (true) {
      case /clients\//.test(currentPath):
      case /exercises\//.test(currentPath):
      case /workouts\/edit/.test(currentPath):
      case /schedules\//.test(currentPath):
      case /settings\//.test(currentPath):
        return history.goBack();
      case /workouts\//.test(currentPath):
        return history.push("/workouts");
      case /settings/.test(currentPath):
      case /clients/.test(currentPath):
      case /workouts/.test(currentPath):
        setToggle(false);
        return history.goBack();
      default:
        return setToggle(false);
    }
  };

  // Effects
  React.useEffect(() => {
    if (
      !/settings/.test(currentPath) &&
      !/clients\//.test(currentPath) &&
      !/exercises\//.test(currentPath) &&
      !/workouts\//.test(currentPath) &&
      !/schedules\//.test(currentPath) &&
      toggle
    ) {
      return setToggle(false);
    }
    if (/clients\//.test(currentPath) && !toggle) {
      return setToggle(true);
    }
    if (/exercises\//.test(currentPath) && !toggle) {
      return setToggle(true);
    }
    if (/workouts\//.test(currentPath) && !toggle) {
      return setToggle(true);
    }
    if (/schedules\//.test(currentPath) && !toggle) {
      return setToggle(true);
    }
    if (/settings\//.test(currentPath) && !toggle) {
      return setToggle(true);
    }
  }, [currentPath, toggle]);

  return (
    <div
      id="Header"
      style={{
        // backgroundImage: `url(${bg_layer})`
        backgroundColor: "#fffff",
      }}
    >
      <span className="brand" style={{ cursor: "pointer" }} onClick={() => history.push("/")}>
        <img
          src={companies && companies.length !== 0 ? companies[0].logo.medium : ic_logo}
          alt="logo"
        />
      </span>
      <h2 style={{ color: "var(--text-primary)" }}>{t(header().trim())}</h2>
      <BaC show={toggle} action={handleBack} />
      {!toggle && iconSpan()}
      {!edit && iconSpan()}
    </div>
  );
};

export const HeaderBottomComponent: React.FC<HeaderProps> = () => {
  const { bg_layer } = Icons;
  return (
    <div
      className="header-bottom"
      style={{
        height: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 125 : 200,
        backgroundImage: `url(${bg_layer})`,
      }}
    />
  );
};

export default withRouter(HeaderComponent);
